import { useLocalization } from "@/context/localization-context";
import { formatAgo, formatCount, formatMs, formatPercent, sum, useNow } from "../../format";
import type { PipelineSection } from "../../types";
import { INVALID_WARN } from "./helpers";
import { StatTile } from "./shared";

/** Totals of the window: orders → captured → evaluated → verdicts, and what is stuck or still running. */
export function PipelineTotals({ data }: { data: PipelineSection }) {
  const { t } = useLocalization();
  const now = useNow();
  const v = data.totals.verdicts;
  const evaluated = v.ok + v.nok + v.invalid_photo + v.no_pizza;
  const photo = v.invalid_photo + v.no_pizza;
  const share = (count: number) => (evaluated ? count / evaluated : null);
  const usualOrders = data.baseline ? sum(data.baseline.orders) : null;
  const usualCaptured = data.baseline ? sum(data.baseline.captured) : null;
  const lastCapture = data.branches.reduce<number | null>(
    (latest, b) => (b.last_capture_at && (!latest || b.last_capture_at > latest) ? b.last_capture_at : latest),
    null,
  );
  // the list of stuck pizzas is capped; the split by "photo received" is of the listed ones
  const withPhoto = data.missing.filter((p) => p.upload_received).length;
  const withoutPhoto = data.missing.length - withPhoto;
  const usual = (value: number | null) =>
    value === null ? undefined : t("monitoring.pipeline.totals.usual", { value: formatCount(Math.round(value)) });

  return (
    <div className="flex flex-col gap-1.5">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 min-[1400px]:grid-cols-9">
        <StatTile label={t("monitoring.series.orders")} value={formatCount(data.totals.orders)} detail={usual(usualOrders)} />
        <StatTile
          label={t("monitoring.series.captured")}
          value={formatCount(data.totals.captured)}
          detail={
            [usual(usualCaptured), lastCapture ? t("monitoring.kpi.lastCapture", { ago: formatAgo(lastCapture, now) }) : null]
              .filter(Boolean)
              .join(" · ") || undefined
          }
        />
        <StatTile
          label={t("monitoring.pipeline.totals.evaluated")}
          value={formatCount(evaluated)}
          detail={t("monitoring.pipeline.totals.evaluatedDetail", {
            share: formatPercent(data.totals.captured ? evaluated / data.totals.captured : null),
          })}
        />
        <StatTile
          label={t("monitoring.verdict.ok")}
          value={formatCount(v.ok)}
          detail={t("monitoring.pipeline.totals.share", { share: formatPercent(share(v.ok)) })}
        />
        <StatTile
          label={t("monitoring.verdict.nok")}
          value={formatCount(v.nok)}
          detail={t("monitoring.pipeline.totals.share", { share: formatPercent(share(v.nok)) })}
        />
        <StatTile
          label={t("monitoring.verdict.photo")}
          value={formatCount(photo)}
          detail={t("monitoring.pipeline.totals.photoDetail", {
            invalid: formatCount(v.invalid_photo),
            noPizza: formatCount(v.no_pizza),
            share: formatPercent(share(photo)),
          })}
          tone={(share(photo) ?? 0) >= INVALID_WARN ? "warn" : "neutral"}
        />
        <StatTile
          label={t("monitoring.verdict.missing")}
          hint={t("monitoring.pipeline.stuck.description")}
          value={formatCount(data.missing_total)}
          detail={
            data.missing_total
              ? t("monitoring.pipeline.totals.stuckDetail", { withPhoto: formatCount(withPhoto), withoutPhoto: formatCount(withoutPhoto) })
              : t("monitoring.pipeline.totals.stuckNone")
          }
          tone={data.missing_total > 0 ? "nok" : "neutral"}
        />
        <StatTile
          label={t("monitoring.verdict.pending")}
          value={formatCount(v.pending)}
          detail={t("monitoring.pipeline.totals.pendingDetail")}
        />
        <StatTile
          label={t("monitoring.pipeline.totals.recaptures")}
          value={formatCount(data.totals.recaptures)}
          detail={t("monitoring.pipeline.totals.recapturesDetail")}
        />
      </div>
      <p className="text-right text-[11px] text-muted-foreground">
        {t("monitoring.pipeline.totals.query", { ms: formatMs(data.query_ms), ago: formatAgo(data.generated_at, now) })}
      </p>
    </div>
  );
}
